// Mock data for AI-ECO Suite — kept colocated for demo simplicity.

export const COMPANY = {
  name: "AI-ECO Suite",
  tagline: "The Control Plane for Enterprise AI",
  env: "Production",
};

// AGRI score breakdown
export const agriBreakdown = [
  { pillar: "Governance", weight: 20, score: 17 },
  { pillar: "Risk", weight: 20, score: 14 },
  { pillar: "Compliance", weight: 20, score: 18 },
  { pillar: "Security & Privacy", weight: 15, score: 12 },
  { pillar: "Human Oversight", weight: 15, score: 13 },
  { pillar: "Auditability", weight: 10, score: 8 },
];
export const agriScore = agriBreakdown.reduce((s, p) => s + p.score, 0); // 82

export const kpis = [
  { key: "ai-assets", label: "AI Assets", value: 47, delta: "+6", trend: "up", icon: "Boxes" },
  { key: "active-agents", label: "Active Agents", value: 12, delta: "+2", trend: "up", icon: "Bot" },
  { key: "critical-risks", label: "Critical Risks", value: 3, delta: "-1", trend: "down", icon: "AlertTriangle" },
  { key: "human-reviews", label: "Human Reviews", value: 8, delta: "+3", trend: "up", icon: "UserCheck" },
  { key: "policy-violations", label: "Policy Violations", value: 14, delta: "-4", trend: "down", icon: "ShieldAlert" },
  { key: "runtime-events", label: "Runtime Events", value: 1284, delta: "+212", trend: "up", icon: "Activity" },
];

// Token usage over last 14 days
export const tokenTrend = Array.from({ length: 14 }).map((_, i) => {
  const base = 800000 + Math.sin(i / 2) * 180000 + i * 25000;
  return {
    day: `D${i + 1}`,
    tokens: Math.round(base),
    cost: Math.round((base / 1000) * 0.004 * 100) / 100,
  };
});

export const tokenStats = {
  today: "1.2M",
  monthly: "32M",
  cost: "$4,850",
  anomalies: 2,
};

export const topConsumers = [
  { name: "OpenAI", tokens: 400000, color: "#5B8DEF" },
  { name: "Claude", tokens: 250000, color: "#22C55E" },
  { name: "Decision Agent", tokens: 150000, color: "#F59E0B" },
  { name: "Applicant Agent", tokens: 120000, color: "#A78BFA" },
  { name: "Risk Agent", tokens: 80000, color: "#EF4444" },
];

export const riskSummary = [
  { name: "Privacy Risk", level: "Medium", value: 54, color: "#F59E0B" },
  { name: "Operational Risk", level: "Low", value: 22, color: "#22C55E" },
  { name: "Policy Risk", level: "High", value: 78, color: "#EF4444" },
  { name: "Overall Risk", level: "Managed", value: 82, color: "#5B8DEF" },
];

export const recentEvents = [
  { id: 1, type: "violation", title: "Output exceeds approval policy", agent: "Decision Agent", time: "2m ago", severity: "critical" },
  { id: 2, type: "review", title: "Human review queued — James Okafor", agent: "Loan Workflow", time: "4m ago", severity: "warning" },
  { id: 3, type: "runtime", title: "Applicant Agent executed (1,500 tokens)", agent: "Applicant Agent", time: "7m ago", severity: "info" },
  { id: 4, type: "agent", title: "Credit Risk Agent scored applicant", agent: "Credit Risk Agent", time: "8m ago", severity: "info" },
  { id: 5, type: "violation", title: "Unauthorized field access: marital_status", agent: "Credit Risk Agent", time: "11m ago", severity: "critical" },
  { id: 6, type: "agent", title: "Policy v2.4 deployed to Production", agent: "Policy Engine", time: "23m ago", severity: "success" },
  { id: 7, type: "runtime", title: "Token spike detected on OpenAI integration", agent: "Token Intelligence", time: "31m ago", severity: "warning" },
];

// Discovery
export const discovery = [
  { id: "openai", name: "OpenAI Integration", type: "LLM Provider", status: "Healthy", risk: "Low", traffic: "412K tok/day", endpoint: "api.openai.com", registered: true },
  { id: "claude", name: "Claude Integration", type: "LLM Provider", status: "Healthy", risk: "Low", traffic: "258K tok/day", endpoint: "api.anthropic.com", registered: true },
  { id: "applicant", name: "Applicant Data Agent", type: "Business Agent", status: "Healthy", risk: "Low", traffic: "120K tok/day", endpoint: "internal://applicant-agent", registered: true },
  { id: "credit", name: "Credit Risk Agent", type: "Business Agent", status: "Review", risk: "Medium", traffic: "80K tok/day", endpoint: "internal://credit-agent", registered: true },
  { id: "decision", name: "Loan Decision Agent", type: "Business Agent", status: "Critical", risk: "High", traffic: "150K tok/day", endpoint: "internal://decision-agent", registered: true },
  { id: "support-bot", name: "Customer Support Bot", type: "AI Assistant", status: "Healthy", risk: "Low", traffic: "44K tok/day", endpoint: "internal://support-bot", registered: false },
  { id: "kyc-agent", name: "KYC Verification Agent", type: "Business Agent", status: "Review", risk: "Medium", traffic: "62K tok/day", endpoint: "internal://kyc-agent", registered: false },
  { id: "embeddings", name: "OpenAI Embeddings", type: "LLM Provider", status: "Healthy", risk: "Low", traffic: "94K tok/day", endpoint: "api.openai.com/v1/embeddings", registered: false },
];

// Inventory & Policies
export const inventory = [
  {
    id: "applicant",
    name: "Applicant Data Agent",
    purpose: "Retrieve and normalize customer information for downstream loan workflows.",
    risk: "Low",
    agri: 91,
    tokens: "120K",
    allowedActions: ["read_profile", "read_employment", "compose_summary"],
    allowedData: ["income", "employment_status", "tenure", "address"],
    blockedData: ["race", "gender", "religion", "political_affiliation"],
    policies: [
      "PII fields must be redacted in logs.",
      "Cannot access protected attributes (race, gender).",
      "All reads emit audit events.",
    ],
  },
  {
    id: "credit",
    name: "Credit Risk Agent",
    purpose: "Score applicants based on credit signals and income capacity.",
    risk: "Medium",
    agri: 76,
    tokens: "80K",
    allowedActions: ["score_applicant", "fetch_credit_report"],
    allowedData: ["credit_score", "income", "debt_to_income", "delinquencies"],
    blockedData: ["marital_status", "race", "gender"],
    policies: [
      "Must NOT read marital_status.",
      "Score must fall within [0, 100].",
      "Decisions above 80 must escalate.",
    ],
  },
  {
    id: "decision",
    name: "Loan Decision Agent",
    purpose: "Make final approve / decline / escalate decision and produce explanation.",
    risk: "High",
    agri: 64,
    tokens: "150K",
    allowedActions: ["approve_loan", "decline_loan", "escalate"],
    allowedData: ["risk_score", "verified_income", "requested_amount"],
    blockedData: ["protected_attributes"],
    policies: [
      "Max approval = 3× verified income.",
      "Risk > 80 → Human review required.",
      "All approvals require an explanation string.",
    ],
  },
];

// Runtime use cases (auto-cycled in Runtime Monitor)
export const useCases = [
  {
    id: "sarah",
    name: "Sarah Chen",
    avatar: "https://images.unsplash.com/photo-1609436132311-e4b0c9370469?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGl2ZXJzZXxlbnwwfHx8fDE3ODIyMDU4NTl8MA&ixlib=rb-4.1.0&q=85",
    income: 60000,
    requested: 45000,
    riskScore: 18,
    tokens: 1500,
    outcome: "Approved",
    status: "success", // green
    policyResult: "All policy checks passed",
    summary: "Risk well within thresholds. Decision auto-approved.",
    steps: [
      { agent: "Applicant Agent", action: "Profile retrieved", tokens: 420, risk: 0, status: "success", time: "09:01:02" },
      { agent: "Credit Agent", action: "Score = 742, DTI healthy", tokens: 380, risk: 6, status: "success", time: "09:01:05" },
      { agent: "Decision Agent", action: "Approve $45,000 (1.33× income)", tokens: 700, risk: 12, status: "success", time: "09:01:09" },
      { agent: "Outcome", action: "Auto-execute → Approved", tokens: 0, risk: 18, status: "success", time: "09:01:10" },
    ],
  },
  {
    id: "james",
    name: "James Okafor",
    avatar: "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    income: 50000,
    requested: 180000,
    riskScore: 95,
    tokens: 2100,
    outcome: "Blocked",
    status: "critical", // red
    policyResult: "Output exceeds policy: Max approval = $150,000",
    summary: "Decision Agent attempted to approve $180K. Policy ceiling = 3× income = $150K. Blocked at outcome boundary.",
    steps: [
      { agent: "Applicant Agent", action: "Profile retrieved", tokens: 500, risk: 0, status: "success", time: "09:14:30" },
      { agent: "Credit Agent", action: "Score = 689, DTI elevated", tokens: 450, risk: 35, status: "warning", time: "09:14:34" },
      { agent: "Decision Agent", action: "Proposed $180K — exceeds 3× income ceiling", tokens: 1150, risk: 95, status: "critical", time: "09:14:39" },
      { agent: "Outcome", action: "BLOCKED → Human Review Required", tokens: 0, risk: 95, status: "critical", time: "09:14:40" },
    ],
  },
  {
    id: "priya",
    name: "Priya Patel",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGl2ZXJzZXxlbnwwfHx8fDE3ODIyMDU4NTl8MA&ixlib=rb-4.1.0&q=85",
    income: 72000,
    requested: 90000,
    riskScore: 85,
    tokens: 1800,
    outcome: "Escalated",
    status: "warning", // amber
    policyResult: "Unauthorized field access: marital_status",
    summary: "Credit Risk Agent attempted to read marital_status — blocked field. Decision escalated for human oversight.",
    steps: [
      { agent: "Applicant Agent", action: "Profile retrieved", tokens: 470, risk: 0, status: "success", time: "09:22:11" },
      { agent: "Credit Agent", action: "Attempted read: marital_status (BLOCKED)", tokens: 530, risk: 75, status: "critical", time: "09:22:14" },
      { agent: "Decision Agent", action: "Insufficient signals — escalate", tokens: 800, risk: 85, status: "warning", time: "09:22:18" },
      { agent: "Outcome", action: "ESCALATED → Human Review", tokens: 0, risk: 85, status: "warning", time: "09:22:19" },
    ],
  },
];

// Human Oversight queue
export const reviewQueue = [
  {
    id: "rev-1042",
    applicant: "James Okafor",
    avatar: "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    violatedPolicy: "Loan Decision Policy v2.4 — Max approval ≤ 3× verified income",
    riskScore: 95,
    agent: "Loan Decision Agent",
    submitted: "Today · 09:14",
    priority: "Critical",
    requested: "$180,000",
    income: "$50,000",
    trace: [
      "09:14:30 — Applicant Agent retrieved profile.",
      "09:14:34 — Credit Agent scored 689 (DTI elevated).",
      "09:14:39 — Decision Agent proposed $180,000.",
      "09:14:40 — Policy Engine blocked → exceeds 3× income.",
      "09:14:40 — Routed to Human Oversight Center.",
    ],
    decisionHistory: [
      { actor: "Policy Engine", action: "Blocked", time: "09:14:40", note: "Auto-block: policy ceiling breached." },
    ],
  },
  {
    id: "rev-1043",
    applicant: "Priya Patel",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwZGl2ZXJzZXxlbnwwfHx8fDE3ODIyMDU4NTl8MA&ixlib=rb-4.1.0&q=85",
    violatedPolicy: "Credit Risk Agent Policy v1.8 — Field marital_status is blocked",
    riskScore: 85,
    agent: "Credit Risk Agent",
    submitted: "Today · 09:22",
    priority: "High",
    requested: "$90,000",
    income: "$72,000",
    trace: [
      "09:22:11 — Applicant Agent retrieved profile.",
      "09:22:14 — Credit Agent attempted read: marital_status.",
      "09:22:14 — Privacy Engine blocked field access.",
      "09:22:18 — Decision Agent escalated (insufficient signals).",
      "09:22:19 — Routed to Human Oversight Center.",
    ],
    decisionHistory: [
      { actor: "Privacy Engine", action: "Blocked field access", time: "09:22:14", note: "marital_status is on the blocked list." },
    ],
  },
];

// Audit trail
export const auditEntries = [
  { id: "ev-9001", time: "09:01:02", agent: "Applicant Agent", action: "read_profile", policy: "Applicant Policy v1.2", risk: 0, tokens: 420, result: "OK", human: "—" },
  { id: "ev-9002", time: "09:01:05", agent: "Credit Agent", action: "score_applicant", policy: "Credit Policy v1.8", risk: 6, tokens: 380, result: "OK", human: "—" },
  { id: "ev-9003", time: "09:01:09", agent: "Decision Agent", action: "approve_loan", policy: "Decision Policy v2.4", risk: 12, tokens: 700, result: "Approved", human: "Auto" },
  { id: "ev-9101", time: "09:14:30", agent: "Applicant Agent", action: "read_profile", policy: "Applicant Policy v1.2", risk: 0, tokens: 500, result: "OK", human: "—" },
  { id: "ev-9102", time: "09:14:34", agent: "Credit Agent", action: "score_applicant", policy: "Credit Policy v1.8", risk: 35, tokens: 450, result: "Warning", human: "—" },
  { id: "ev-9103", time: "09:14:39", agent: "Decision Agent", action: "approve_loan", policy: "Decision Policy v2.4", risk: 95, tokens: 1150, result: "Blocked", human: "Pending" },
  { id: "ev-9104", time: "09:14:40", agent: "Policy Engine", action: "evaluate_output", policy: "Decision Policy v2.4", risk: 95, tokens: 0, result: "Violation", human: "Pending" },
  { id: "ev-9201", time: "09:22:11", agent: "Applicant Agent", action: "read_profile", policy: "Applicant Policy v1.2", risk: 0, tokens: 470, result: "OK", human: "—" },
  { id: "ev-9202", time: "09:22:14", agent: "Credit Agent", action: "read_field:marital_status", policy: "Credit Policy v1.8", risk: 75, tokens: 530, result: "Blocked", human: "Pending" },
  { id: "ev-9203", time: "09:22:18", agent: "Decision Agent", action: "escalate", policy: "Decision Policy v2.4", risk: 85, tokens: 800, result: "Escalated", human: "Pending" },
  { id: "ev-9301", time: "10:02:11", agent: "Support Bot", action: "answer_query", policy: "Support Policy v0.9", risk: 5, tokens: 220, result: "OK", human: "—" },
  { id: "ev-9302", time: "10:11:46", agent: "KYC Agent", action: "verify_id", policy: "KYC Policy v3.1", risk: 18, tokens: 640, result: "OK", human: "—" },
  { id: "ev-9303", time: "10:24:02", agent: "Decision Agent", action: "decline_loan", policy: "Decision Policy v2.4", risk: 22, tokens: 540, result: "Declined", human: "Auto" },
  { id: "ev-9304", time: "10:38:55", agent: "Applicant Agent", action: "compose_summary", policy: "Applicant Policy v1.2", risk: 2, tokens: 390, result: "OK", human: "—" },
];

export const agriBands = [
  { label: "Governed", min: 90, max: 100, color: "#22C55E" },
  { label: "Managed", min: 80, max: 89, color: "#5B8DEF" },
  { label: "Controlled", min: 70, max: 79, color: "#F59E0B" },
  { label: "Attention", min: 60, max: 69, color: "#F59E0B" },
  { label: "High Risk", min: 0, max: 59, color: "#EF4444" },
];

export function agriBandFor(score) {
  if (score >= 90) return { label: "Governed", color: "#22C55E" };
  if (score >= 80) return { label: "Managed", color: "#5B8DEF" };
  if (score >= 70) return { label: "Controlled", color: "#F59E0B" };
  if (score >= 60) return { label: "Attention Required", color: "#F59E0B" };
  return { label: "High Risk", color: "#EF4444" };
}
